import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
} from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';

const TERMS_META = {
  lastUpdated: 'March 2, 2025',
  legalContactEmail: 'contact@penchukcyber.com',
  companyLegalName: 'Penchuk Cyber Ltd.',
  companyAddress: 'Azrieli Sarona Tower, 121-123 Derech Menachem Begin, Tel-Aviv, Israel',
  websiteUrl: 'www.penchukcyber.com',
  jurisdiction: 'State of Israel',
};

const Terms: React.FC = () => {
  const sectionTitleProps = { sx: { mt: 3, mb: 1.5 } };
  const subsectionProps = { sx: { mt: 2, mb: 1 } };
  const bodyProps = { variant: 'body1' as const, sx: { mb: 1.5, maxWidth: '72ch', lineHeight: 1.6 } };
  const listProps = { component: 'ul' as const, sx: { pl: 2.5, mb: 1.5 } };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
      }}
    >
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: 1.5,
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" component="h1">
              Terms of Use
            </Typography>
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              startIcon={<HomeIcon />}
              sx={{ textDecoration: 'none' }}
            >
              Home
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 3, px: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Last Updated: {TERMS_META.lastUpdated}
        </Typography>

        {/* 1. Introduction */}
        <Typography variant="h5" {...sectionTitleProps}>
          1. Introduction
        </Typography>
        <Typography {...bodyProps}>
          Welcome to CaosBlitz (&quot;Company&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;).
        </Typography>
        <Typography {...bodyProps}>
          These Terms of Use (&quot;Terms&quot;) govern your access to and use of the CaosBlitz website, platform, DDoS simulation tools, analytics dashboards, APIs, reports, and related services (collectively, the &quot;Services&quot;).
        </Typography>
        <Typography {...bodyProps}>
          By accessing or using the Services, you agree to be legally bound by these Terms. If you do not agree, you must not use the Services.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 2. Description of Services */}
        <Typography variant="h5" {...sectionTitleProps}>
          2. Description of Services
        </Typography>
        <Typography {...bodyProps}>
          CaosBlitz provides a cybersecurity simulation platform designed to help organizations assess, test, and improve resilience against Distributed Denial-of-Service (DDoS) attacks.
        </Typography>
        <Typography {...bodyProps}>
          The platform may simulate various categories of DDoS attack patterns, including:
        </Typography>
        <Typography {...listProps}>
          <li>Volumetric attacks</li>
          <li>Infrastructure-level attacks</li>
          <li>Application-layer attacks</li>
          <li>Advanced or custom traffic scenarios</li>
        </Typography>
        <Typography {...bodyProps}>
          All simulations are intended strictly for authorized security testing and resilience improvement.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 3. Eligibility */}
        <Typography variant="h5" {...sectionTitleProps}>
          3. Eligibility
        </Typography>
        <Typography {...bodyProps}>
          You may use the Services only if:
        </Typography>
        <Typography {...listProps}>
          <li>You are at least 18 years old</li>
          <li>You are legally capable of entering into binding agreements</li>
          <li>You are acting on behalf of a lawful organization or with proper authority</li>
        </Typography>
        <Typography {...bodyProps}>
          If you use the Services on behalf of an organization, you represent that you have authority to bind that organization to these Terms.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 4. Authorized Use Only */}
        <Typography variant="h5" {...sectionTitleProps}>
          4. Authorized Use Only
        </Typography>

        <Typography variant="h6" {...subsectionProps}>
          4.1 Explicit Authorization Required
        </Typography>
        <Typography {...bodyProps}>
          You may only use CaosBlitz to test systems, networks, domains, IP addresses, APIs, or infrastructure for which you have explicit, documented authorization.
        </Typography>
        <Typography {...bodyProps}>
          You agree that:
        </Typography>
        <Typography {...listProps}>
          <li>You will not simulate attacks against systems you do not own or have permission to test.</li>
          <li>You will maintain written authorization records.</li>
          <li>You are solely responsible for ensuring compliance with applicable laws.</li>
        </Typography>

        <Typography variant="h6" {...subsectionProps}>
          4.2 Prohibited Uses
        </Typography>
        <Typography {...bodyProps}>
          You agree NOT to:
        </Typography>
        <Typography {...listProps}>
          <li>Use the platform for unlawful, malicious, or harmful purposes</li>
          <li>Conduct unauthorized DDoS attacks</li>
          <li>Attempt to disrupt third-party infrastructure</li>
          <li>Use the platform to cause real-world service outages beyond agreed test scope</li>
          <li>Reverse engineer, exploit, or abuse the platform</li>
          <li>Use CaosBlitz to evade law enforcement or defensive mechanisms</li>
        </Typography>
        <Typography {...bodyProps}>
          Violation of this section may result in immediate suspension or termination and possible legal action.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 5. Account Registration and Security */}
        <Typography variant="h5" {...sectionTitleProps}>
          5. Account Registration and Security
        </Typography>
        <Typography {...bodyProps}>
          To access certain features, you must create an account.
        </Typography>
        <Typography {...bodyProps}>
          You agree to:
        </Typography>
        <Typography {...listProps}>
          <li>Provide accurate and complete information</li>
          <li>Maintain the confidentiality of login credentials</li>
          <li>Notify us immediately of unauthorized access</li>
          <li>Be responsible for all activities under your account</li>
        </Typography>
        <Typography {...bodyProps}>
          We reserve the right to suspend accounts suspected of misuse.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 6. Compliance With Laws */}
        <Typography variant="h5" {...sectionTitleProps}>
          6. Compliance With Laws
        </Typography>
        <Typography {...bodyProps}>
          You are solely responsible for ensuring that your use of the Services complies with:
        </Typography>
        <Typography {...listProps}>
          <li>Local and international cybersecurity laws</li>
          <li>Data protection regulations</li>
          <li>Telecommunications regulations</li>
          <li>Industry compliance requirements</li>
        </Typography>
        <Typography {...bodyProps}>
          CaosBlitz does not provide legal advice, and use of the platform does not guarantee regulatory compliance.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 7. Simulation Risks and Acknowledgment */}
        <Typography variant="h5" {...sectionTitleProps}>
          7. Simulation Risks and Acknowledgment
        </Typography>
        <Typography {...bodyProps}>
          You acknowledge that:
        </Typography>
        <Typography {...listProps}>
          <li>Security simulations may impact system performance</li>
          <li>Improper configuration may cause unintended service disruption</li>
          <li>You are responsible for testing within controlled and authorized environments</li>
          <li>You assume full responsibility for consequences arising from simulations</li>
        </Typography>
        <Typography {...bodyProps}>
          We strongly recommend internal change management, stakeholder notification, and rollback planning prior to executing simulations.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 8. Intellectual Property */}
        <Typography variant="h5" {...sectionTitleProps}>
          8. Intellectual Property
        </Typography>
        <Typography {...bodyProps}>
          All platform content, software, documentation, reports templates, dashboards, designs, trademarks, and proprietary technology remain the exclusive property of CaosBlitz or its licensors.
        </Typography>
        <Typography {...bodyProps}>
          You may not:
        </Typography>
        <Typography {...listProps}>
          <li>Copy or redistribute proprietary software</li>
          <li>Modify or create derivative works</li>
          <li>Resell or sublicense the Services without written consent</li>
        </Typography>
        <Typography {...bodyProps}>
          Simulation reports generated from your own data remain yours, subject to our underlying platform rights.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 9. Confidentiality */}
        <Typography variant="h5" {...sectionTitleProps}>
          9. Confidentiality
        </Typography>
        <Typography {...bodyProps}>
          We treat customer simulation configurations and reports as confidential.
        </Typography>
        <Typography {...bodyProps}>
          You agree not to disclose:
        </Typography>
        <Typography {...listProps}>
          <li>Platform methodologies</li>
          <li>Proprietary simulation logic</li>
          <li>Internal testing frameworks</li>
        </Typography>
        <Typography {...bodyProps}>
          Unless required by law.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 10. Data Protection */}
        <Typography variant="h5" {...sectionTitleProps}>
          10. Data Protection
        </Typography>
        <Typography {...bodyProps}>
          Our data handling practices are described in our Privacy Policy, which is incorporated into these Terms by reference.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 11. Service Availability */}
        <Typography variant="h5" {...sectionTitleProps}>
          11. Service Availability
        </Typography>
        <Typography {...bodyProps}>
          We aim to maintain high availability but do not guarantee uninterrupted service.
        </Typography>
        <Typography {...bodyProps}>
          We may:
        </Typography>
        <Typography {...listProps}>
          <li>Modify features</li>
          <li>Suspend services for maintenance</li>
          <li>Update simulation capabilities</li>
          <li>Restrict access in case of misuse</li>
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 12. Disclaimers */}
        <Typography variant="h5" {...sectionTitleProps}>
          12. Disclaimers
        </Typography>
        <Typography {...bodyProps}>
          The Services are provided &quot;as is&quot; and &quot;as available.&quot;
        </Typography>
        <Typography {...bodyProps}>
          We do not guarantee:
        </Typography>
        <Typography {...listProps}>
          <li>That simulations replicate real-world attacks exactly</li>
          <li>That vulnerabilities will be fully identified</li>
          <li>That mitigation systems will perform as expected</li>
          <li>That the platform will be error-free</li>
        </Typography>
        <Typography {...bodyProps}>
          CaosBlitz is a testing and simulation tool, not a guarantee of security.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 13. Limitation of Liability */}
        <Typography variant="h5" {...sectionTitleProps}>
          13. Limitation of Liability
        </Typography>
        <Typography {...bodyProps}>
          To the maximum extent permitted by law:
        </Typography>
        <Typography {...bodyProps}>
          CaosBlitz shall not be liable for:
        </Typography>
        <Typography {...listProps}>
          <li>Indirect, incidental, or consequential damages</li>
          <li>Business interruption</li>
          <li>Loss of profits or data</li>
          <li>Regulatory penalties resulting from misuse</li>
          <li>Damages arising from unauthorized testing</li>
        </Typography>
        <Typography {...bodyProps}>
          Total liability shall not exceed the fees paid by you in the preceding 12 months.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 14. Indemnification */}
        <Typography variant="h5" {...sectionTitleProps}>
          14. Indemnification
        </Typography>
        <Typography {...bodyProps}>
          You agree to indemnify and hold harmless CaosBlitz and its affiliates from any claims, damages, fines, or liabilities arising from:
        </Typography>
        <Typography {...listProps}>
          <li>Unauthorized testing</li>
          <li>Violation of these Terms</li>
          <li>Violation of laws</li>
          <li>Misuse of the platform</li>
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 15. Termination */}
        <Typography variant="h5" {...sectionTitleProps}>
          15. Termination
        </Typography>
        <Typography {...bodyProps}>
          We may suspend or terminate your access if:
        </Typography>
        <Typography {...listProps}>
          <li>You violate these Terms</li>
          <li>We suspect unlawful activity</li>
          <li>Required by law</li>
        </Typography>
        <Typography {...bodyProps}>
          You may terminate your account at any time, subject to outstanding obligations.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 16. Export Control */}
        <Typography variant="h5" {...sectionTitleProps}>
          16. Export Control
        </Typography>
        <Typography {...bodyProps}>
          You agree to comply with all applicable export control and sanctions laws. The Services may not be used in jurisdictions where prohibited.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 17. Modifications to Terms */}
        <Typography variant="h5" {...sectionTitleProps}>
          17. Modifications to Terms
        </Typography>
        <Typography {...bodyProps}>
          We may update these Terms periodically. Updates will be posted with a revised &quot;Last Updated&quot; date.
        </Typography>
        <Typography {...bodyProps}>
          Continued use of the Services constitutes acceptance of revised Terms.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 18. Governing Law */}
        <Typography variant="h5" {...sectionTitleProps}>
          18. Governing Law
        </Typography>
        <Typography {...bodyProps}>
          These Terms shall be governed by the laws of the {TERMS_META.jurisdiction}.
        </Typography>
        <Typography {...bodyProps}>
          Disputes shall be resolved in the competent courts of that jurisdiction.
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* 19. Contact Information */}
        <Typography variant="h5" {...sectionTitleProps}>
          19. Contact Information
        </Typography>
        <Typography {...bodyProps}>
          CaosBlitz
        </Typography>
        <Box component="address" sx={{ fontStyle: 'normal', ...bodyProps.sx }}>
          <Typography {...bodyProps}>
            {TERMS_META.companyLegalName}
            <br />
            {TERMS_META.companyAddress}
            <br />
            Email: {TERMS_META.legalContactEmail}
            <br />
            Website: {TERMS_META.websiteUrl}
          </Typography>
        </Box>

        <Box sx={{ mt: 4, mb: 2 }}>
          <Button
            component={Link}
            to="/login"
            variant="contained"
            startIcon={<HomeIcon />}
            sx={{ textDecoration: 'none' }}
          >
            Back to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Terms;
